import { Field, useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { CERTIFICATE_TYPES } from "../../../constants/certificate-constants";
import { domainNameSystemListValidation, internetProtocolListValidation, uniformResourceIdentifierListValidation } from "../../../utilities/Validation";
import Label from "../../label/label";
import { WizardStepLayout } from "../../wizard/wizard-summary-layout";

interface SubjectAlternativeNameProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement>) => void;
    setStepsDescription: React.Dispatch<React.SetStateAction<{ details: string; encryption: string; subjectAlternativeName: string }>>;
}

const SubjectAlternativeName: FC<SubjectAlternativeNameProps> = ({ onNext, onBack, setStepsDescription }) => {
    const { translate } = useLocale();
    const { errors, values } = useFormikContext<any>();

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStepsDescription((prev) => ({
            ...prev,
            subjectAlternativeName: values.domainNameSystem
        }));
        onNext(e);
    }

    /* 
     * Validate the form fields to determine if the "Next" button should be enabled 
     */
    const isValid = (): boolean => {

        // If DNS is required but not populated, return false
        if (isDnsRequired() && !values.domainNameSystem) {
            return false;
        }
        // If URI is required but not populated, return false
        if (isUriRequired() && !values.uniformResourceIdentifier) { 
            return false;
        }
        // If there are any errors, return false
        if (errors.domainNameSystem || errors.internetProtocol || errors.uniformResourceIdentifier) {
            return false;
        }

        // If all validations pass, return true
        return true;
    }

    /*
     * DNS is required for CA, Controller MQTT, Software MQTT and Broker certificates.
     * For all other types, DNS is optional.
     */
    const isDnsRequired = (): boolean => {
        return values.isCA || values.certificateTypeId === CERTIFICATE_TYPES.CONTROLLER_MQTT
            || values.certificateTypeId === CERTIFICATE_TYPES.SOFTWARE_MQTT
            || values.certificateTypeId === CERTIFICATE_TYPES.MQTT_BROKER;
    }

    /*
     * URI is required for Webserver certificates.
     * For all other types, URI is optional.
     */
    const isUriRequired = (): boolean => {
        return values.certificateTypeId === CERTIFICATE_TYPES.WEBSERVER;
    }

    return <WizardStepLayout onBack={onBack} onNext={onLocalNext} nextDisabled={!isValid()}>
        <span className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>{translate.byKey(TranslationKeys.subjectAlternativeName)}</span>

        <div className='flex flex-col space-y-2'>
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='domainNameSystemLabelId'>
                    <Label
                        htmlFor="domainNameSystem"
                        className="text-xs text-black mt-1"
                        required={isDnsRequired()}>{translate.byKey(TranslationKeys.domainNameSystem)}:</Label>
                    {errors.domainNameSystem && values.isCA ? <span className='text-red-600 text-xs'>{errors.domainNameSystem}</span> : null}
                </div>
                <Field id='domainNameSystemInputFieldId'
                    type="text"
                    name="domainNameSystem"
                    autoFocus
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={(value) => domainNameSystemListValidation(value, translate)} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='internetProtocolLabelId'>
                    <Label
                        htmlFor="internetProtocol"
                        className="text-xs text-black mt-1">{translate.byKey(TranslationKeys.internetProtocol)}:</Label>
                    {errors.internetProtocol ? <span className='text-red-600 text-xs'>{errors.internetProtocol}</span> : null}
                </div>
                <Field id='internetProtocolInputFieldId'
                    type="text"
                    name="internetProtocol"
                    autoFocus
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={(value) => internetProtocolListValidation(value, !values.isCA, translate)} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='uniformResourceIdentifierLabelId'>
                    <Label
                        htmlFor="uniformResourceIdentifier"
                        required={isUriRequired()}
                        className="text-xs text-black mt-1">{translate.byKey(TranslationKeys.uniformResourceIdentifier)}:</Label>
                    {errors.uniformResourceIdentifier ? <span className='text-red-600 text-xs'>{errors.uniformResourceIdentifier}</span> : null}
                </div>
                <Field id='uniformResourceIdentifierInputFieldId'
                    type="text"
                    name="uniformResourceIdentifier"
                    autoFocus
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={(value) => uniformResourceIdentifierListValidation(value, translate)} />
            </div>
        </div>
    </WizardStepLayout>
}

export default SubjectAlternativeName;